import { FrameNotFound } from '@Components/errorHandling';
import { AGENTIC_ORCHESTRATOR_API } from '@Constants/backendConstants';
import { workflowRunStepColumns } from '@Constants/TableColumns';
import {
    getBadgeVariantByWorkflowRunStatus,
    getEnumWorkflowRunStatusLabel,
    type ApiWorkflowRun,
    type ApiWorkflowRunStep,
} from '@Model';
import { useWorkflowRunStory } from '@Hooks/AgenticOrchestrator/useWorkflowStory';
import { FrameEditCommandAuditLog } from '@Modules/Auditing';
import { workflowTitleOf } from '../workflowTools';
import { WorkflowRunStoryCard } from './WorkflowRunStoryCard';
import frameClasses from '../../readOnlyFrameContent.module.css';
import {
    Card,
    DateInputBase,
    EdgeSpacer,
    FlexColumn,
    FrameHeader,
    Grid,
    GridColumns,
    isNullOrEmpty,
    LoadingFrame,
    ScrollContainer,
    TableWithStoredSettings,
    TextInputBase,
    useFrameEditBaseNavigation,
    useOnTheFlyModal,
    useServerObject,
} from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { useAgenticOrchestratorRepository } from '@Hooks/useRepository';
import { WorkflowRunsRepository } from '@Repositories/AgenticOrchestrator';
import { emptyPage } from '@Hooks/useListApi';
import { type ListRequest, unwrapOr } from '@synqo/frontend-component-library';

export interface FrameEditWorkflowRunProps {
    /**
     * When set, this row PK is used instead of the route param. Enables opening the
     * frame inside an on-the-fly modal from other views (e.g. the definition detail).
     */
    primaryKey?: string;
    /**
     * When true the header back button delegates to {@link onClose} instead of route navigation.
     */
    embedded?: boolean;
    /**
     * Called when the header back button is used in an embedded context.
     * Only relevant when {@link embedded} is true.
     */
    onClose?: () => void;
}

export function FrameEditWorkflowRun(props: FrameEditWorkflowRunProps = {}) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const { result, isSuccess } = useServerObject<ApiWorkflowRun>({
        apiResourcePath: `${AGENTIC_ORCHESTRATOR_API}/workflow_runs`,
        primaryKey,
        include: ['all'],
    });

    if (isSuccess === null) {
        return <LoadingFrame />;
    }
    if (!isSuccess) {
        return (
            <FrameNotFound
                apiResourcePath={`${AGENTIC_ORCHESTRATOR_API}/workflow_runs`}
                primaryKey={primaryKey}
                result={result}
                embedded={props.embedded ?? false}
            />
        );
    }

    return <FrameEditWorkflowRunBody data={result.data} embedded={props.embedded} onClose={props.onClose} />;
}

interface FrameEditWorkflowRunBodyProps {
    data: ApiWorkflowRun;
    embedded?: boolean;
    onClose?: () => void;
}

const lockedOnChange = () => false;

function FrameEditWorkflowRunBody({ data, embedded, onClose }: FrameEditWorkflowRunBodyProps) {
    const { onBack: routeBack } = useFrameEditBaseNavigation();
    const onBack = embedded ? onClose : routeBack;
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    // Steps are queried through the run-scoped route so the WorkflowRunId filter is applied
    // server-side (mirrors the command-audit-log → related-action-events pattern).
    const workflowRuns = useAgenticOrchestratorRepository(WorkflowRunsRepository);
    const loadRelatedSteps = useCallback(
        async (listRequest: ListRequest) =>
            unwrapOr(await workflowRuns.getSteps(data.id, listRequest, ''), emptyPage<ApiWorkflowRunStep>()),
        [workflowRuns, data.id],
    );

    // Clicking a step row with a CommandInvocationId opens the command-audit-log detail in an
    // on-the-fly modal — same interaction we use in FrameEditCommandAuditLog for the
    // related action events.
    const openCommandModal = useCallback(
        (step: ApiWorkflowRunStep) => {
            if (isNullOrEmpty(step.command_invocation_id)) {
                return false;
            }
            let modalId = '';
            modalId = openOnTheFlyModal({
                child: (
                    <FrameEditCommandAuditLog
                        invocationId={step.command_invocation_id}
                        embedded
                        onClose={() => closeOnTheFlyModals(modalId)}
                    />
                ),
            });
            return true;
        },
        [openOnTheFlyModal, closeOnTheFlyModals],
    );

    const stepsSorting = useMemo(() => ({ columnName: 'created_at' as const, direction: 'asc' as const }), []);

    const { view: story } = useWorkflowRunStory(data.id);

    return (
        <FlexColumn gap="none" height="100%" width="100%">
            <FrameHeader label="Workflow-Lauf" resourceLabel="Workflow-Lauf" onBack={onBack} />
            <ScrollContainer width="100%" height="100%">
                <FlexColumn gap="sm" className={frameClasses.content}>
                    <Card
                        name="HeadData"
                        label="Lauf"
                        badges={[
                            {
                                children: getEnumWorkflowRunStatusLabel(data.run_status),
                                variant: getBadgeVariantByWorkflowRunStatus(data.run_status),
                            },
                        ]}
                    >
                        <Grid columns={12}>
                            <GridColumns spans={[4, 4, 4]}>
                                <TextInputBase
                                    name="definition_key"
                                    label="Workflow"
                                    value={story?.title ?? workflowTitleOf(data.definition_key)}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <DateInputBase
                                    name="created_at"
                                    label="Gestartet"
                                    type="datetime"
                                    value={data.created_at}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <DateInputBase
                                    name="finished_at"
                                    label="Beendet"
                                    type="datetime"
                                    value={data.finished_at ?? ''}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>

                    <WorkflowRunStoryCard workflowRunId={data.id} />

                    <Card name="Steps" label="Schritte" closed>
                        <TableWithStoredSettings<ApiWorkflowRunStep>
                            name="WorkflowRunSteps"
                            columns={workflowRunStepColumns}
                            initialSorting={stepsSorting}
                            loader={loadRelatedSteps}
                            canCreate={false}
                            onSelect={openCommandModal}
                        />
                    </Card>
                </FlexColumn>
                <EdgeSpacer />
            </ScrollContainer>
        </FlexColumn>
    );
}
